import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Archive, ArchiveRestore, Save, X, Info, Search, Upload } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import ImportWizard from "../../components/app/ImportWizard";

export default function Tasks() {
  const { activeWorkspace, user } = useAuth();
  const { toast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null); // task obj or null
  const [importing, setImporting] = useState(false);

  const load = async () => {
    const [t, m] = await Promise.all([
      api.tasks(showArchived),
      activeWorkspace?.id ? api.members(activeWorkspace.id).catch(() => []) : Promise.resolve([]),
    ]);
    setTasks(t);
    setMembers(m);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspace?.id, showArchived]);

  const myRole = members.find((m) => m.user_id === user?.id)?.role;
  const canEdit = myRole === "owner" || myRole === "admin" || activeWorkspace?.is_platform_access;

  const filtered = useMemo(() => {
    if (!q.trim()) return tasks;
    const needle = q.toLowerCase();
    return tasks.filter((t) => t.name.toLowerCase().includes(needle));
  }, [tasks, q]);

  const archive = async (t) => {
    if (!window.confirm(`Archive "${t.name}"? It'll disappear from project selectors but keeps existing time entries intact.`)) return;
    await api.deleteTask(t.id);
    await load();
    toast({ title: "Task archived", description: t.name });
  };

  const restore = async (t) => {
    await api.updateTask(t.id, { is_archived: false });
    await load();
    toast({ title: "Task restored" });
  };

  return (
    <div data-testid="tasks-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-[#6a736f]">Tasks</div>
          <h1 className="font-display text-4xl mt-1">Task library</h1>
          <p className="text-sm text-[#3f4a44] mt-2 max-w-xl">
            Reusable activities you can attach to any project — e.g. Discovery, Design,
            Development. Set a baseline internal cost per hour so reports show what each
            kind of work actually costs vs earns.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={() => setImporting(true)}
              data-testid="import-tasks-btn"
              className="btn-outline-forest inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
            >
              <Upload size={16} /> Import CSV
            </button>
            <button
              onClick={() => setOpenNew(true)}
              data-testid="open-new-task"
              className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
            >
              <Plus size={16} /> New task
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-[#6a736f]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks…" data-testid="tasks-search"
            className="pl-8 pr-3 py-1.5 rounded-md border border-cream text-sm w-64"
          />
        </div>
        <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox" checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="tasks-show-archived"
          /> Show archived
        </label>
        <div className="text-xs text-[#6a736f] ml-auto">{filtered.length} tasks</div>
      </div>

      <div className="mt-4 bg-white rounded-xl border border-cream overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
            <tr className="text-left">
              <th className="px-4 py-3">Task</th>
              <th className="px-4 py-3 text-right">Baseline unit cost</th>
              <th className="px-4 py-3">Default billable</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EAF3EE]">
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#6a736f]" data-testid="tasks-empty">
                No tasks yet. Create one to get started — you'll be able to attach it to projects and track the time spent.
              </td></tr>
            ) : filtered.map((t) => (
              <tr key={t.id} className="hover:bg-cream-2/40" data-testid={`task-row-${t.id}`}>
                <td className="px-4 py-3 font-medium">{t.name}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {t.default_cost > 0 ? `$${t.default_cost.toFixed(2)}/hr` : <span className="text-[#6a736f]">—</span>}
                </td>
                <td className="px-4 py-3">
                  {t.default_billable
                    ? <span className="text-xs bg-emerald-100 text-emerald-800 rounded px-2 py-0.5">Billable</span>
                    : <span className="text-xs bg-cream text-[#3f4a44] rounded px-2 py-0.5">Non-billable</span>}
                </td>
                <td className="px-4 py-3">
                  {t.is_archived
                    ? <span className="text-xs bg-red-100 text-red-800 rounded px-2 py-0.5">Archived</span>
                    : <span className="text-xs text-[#6a736f]">Active</span>}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  {canEdit && !t.is_archived && (
                    <>
                      <button
                        onClick={() => setEditing(t)}
                        data-testid={`edit-task-${t.id}`}
                        className="text-forest hover:text-coral inline-flex items-center gap-1 text-xs"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => archive(t)}
                        data-testid={`archive-task-${t.id}`}
                        className="text-[#6a736f] hover:text-red-600 inline-flex items-center gap-1 text-xs"
                      >
                        <Archive size={12} /> Archive
                      </button>
                    </>
                  )}
                  {canEdit && t.is_archived && (
                    <button
                      onClick={() => restore(t)}
                      data-testid={`restore-task-${t.id}`}
                      className="text-forest hover:text-coral inline-flex items-center gap-1 text-xs"
                    >
                      <ArchiveRestore size={12} /> Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!canEdit && (
        <div className="mt-4 text-xs text-[#6a736f] flex items-center gap-1">
          <Info size={12} /> Only owners and admins can edit tasks. Ask your workspace owner if you'd like a change.
        </div>
      )}

      {(openNew || editing) && (
        <TaskFormModal
          initial={editing}
          onClose={() => { setOpenNew(false); setEditing(null); }}
          onSaved={async () => { setOpenNew(false); setEditing(null); await load(); }}
          toast={toast}
        />
      )}
      <ImportWizard
        entity="tasks"
        open={importing}
        onClose={() => setImporting(false)}
        onImported={() => load()}
        toast={toast}
      />
    </div>
  );
}

function TaskFormModal({ initial, onClose, onSaved, toast }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    default_cost: initial?.default_cost ?? 0,
    default_billable: initial?.default_billable ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (isEdit) {
        await api.updateTask(initial.id, {
          name: form.name.trim(),
          default_cost: Number(form.default_cost),
          default_billable: !!form.default_billable,
        });
        toast({ title: "Task updated" });
      } else {
        await api.createTask({
          name: form.name.trim(),
          default_cost: Number(form.default_cost),
          default_billable: !!form.default_billable,
        });
        toast({ title: "Task created" });
      }
      await onSaved?.();
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't save task."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-xl w-full max-w-md p-6 space-y-4"
            data-testid="task-form-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">{isEdit ? "Edit task" : "New task"}</h2>
          <button type="button" onClick={onClose} className="text-[#6a736f]"><X size={18} /></button>
        </div>
        <label className="block text-sm">
          <span className="font-medium">Name</span>
          <input required autoFocus value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="task-name-input"
                 className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
        </label>
        <label className="block text-sm">
          <span className="font-medium">Baseline unit cost ($ / hour)</span>
          <input type="number" min={0} step="0.01" value={form.default_cost}
                 onChange={(e) => setForm({ ...form, default_cost: e.target.value })}
                 data-testid="task-cost-input"
                 className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          <span className="text-[11px] text-[#6a736f]">Internal cost per hour. Overridable on each project.</span>
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.default_billable}
                 onChange={(e) => setForm({ ...form, default_billable: e.target.checked })}
                 data-testid="task-billable-checkbox" />
          <span>Billable by default</span>
        </label>
        {err && <div className="text-xs text-red-600" data-testid="task-form-error">{err}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
                  className="btn-outline-forest px-4 py-2 rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy}
                  data-testid="task-save-btn"
                  className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">
            <Save size={14} /> {busy ? "Saving…" : "Save task"}
          </button>
        </div>
      </form>
    </div>
  );
}
